import logger from '@/lib/logger';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MeasurementWizard } from '@/components/MeasurementWizard';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useConnectionWizard } from '@/features/device/hooks/useConnectionWizard';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';

/**
 * New Measurement Page
 * 
 * This page renders the full-screen measurement wizard with optional mock mode.
 * 
 * Usage:
 * - Normal mode: /measurement/new (requires device connection)
 * - Mock mode (no device required): /measurement/new?mock=true
 * 
 * Real mode validates device connection before starting wizard.
 * If no device is connected, shows "Connect Device" screen.
 */
export const NewMeasurement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { open: openConnectionWizard } = useConnectionWizard();
  
  // Check if mock mode is enabled via query parameter
  const mockMode = searchParams.get('mock') === 'true';
  const connectionState = useDeviceStore((s) => s.connectionState);
  // Only react to genuine physical disconnection — 'busy' means measurement is running
  const isDisconnected = connectionState === 'disconnected';
  const reconnectAttemptFailed = useDeviceStore((s) => s.reconnectAttemptFailed);
  // If there's an in-flight measurement, the recovery overlay handles reconnection —
  // do NOT bail out to the connection wizard or the wizard unmount will destroy the
  // persisted state before recovery can start.
  const hasPendingRecovery = useMeasurementWizardStore((s) => s.measurementInFlight !== null);

  // Capture whether a device was already connected when this page mounted.
  // If true, mid-session disconnects are handled by MeasurementWizard's own
  // blocking dialog so we must NOT navigate away and destroy the wizard state.
  const wasConnectedOnMountRef = useRef(connectionState === 'connected');

  // Check device connection on mount (real mode only)
  useEffect(() => {
    if (mockMode) {
      // Mock mode: no check needed
      return;
    }

    // If recovery is pending, let the MeasurementRecoveryOverlay handle it —
    // navigating away here would unmount MeasurementWizard → resetWizard() →
    // clearing measurementInFlight before recovery can begin.
    if (hasPendingRecovery) {
      logger.debug('[NewMeasurement] measurementInFlight present — recovery overlay will handle reconnection, suppressing connection wizard');
      return;
    }

    if (isDisconnected) {
      if (!wasConnectedOnMountRef.current) {
        // Device was never connected (e.g. user navigated directly to the URL) —
        // redirect to home. Only open the connection wizard if this is a genuine
        // fresh visit; if a silent boot reconnect just failed the AppBar chip
        // already communicates the failure, so suppress the wizard.
        logger.debug('[NewMeasurement] No device on mount — redirecting to connection wizard');
        navigate('/');
        if (!reconnectAttemptFailed) {
          openConnectionWizard();
        }
      }
      // Otherwise the device was connected and then unplugged during the wizard
      // session.  MeasurementWizard renders its own WizardDeviceDisconnectedDialog
      // to let the user reconnect without losing their step progress.
    }
  }, [mockMode, isDisconnected, reconnectAttemptFailed, hasPendingRecovery, openConnectionWizard, navigate]);

  const handleCancel = () => {
    navigate('/');
  };

  // Render wizard if device connected (or mock mode)
  // If device is not connected in real mode, the useEffect above will open the connection wizard
  // and user will be redirected elsewhere, so we just show a loading state
  return (
    <MeasurementWizard
      onCancel={handleCancel}
      mockMode={mockMode}
    />
  );
};
